import styled from '@emotion/styled';
import { GridList as _GridList, GridListTile, GridListTileBar, IconButton, Tooltip } from '@material-ui/core';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxOutlineIcon from '@material-ui/icons/CheckBoxOutlined';
import PlayCircleFilledIcon from '@material-ui/icons/PlayCircleFilled';
import { createElement, Dispatch, FC, forwardRef, Fragment, RefObject, SetStateAction, useMemo } from 'react';
import { Waypoint } from 'react-waypoint';

import { SearchAssetResult } from '../../../../entity/SearchAsset';
import Spinner from '../Spinner';

type Props = {
  data: SearchAssetResult;
  searchDataRef: RefObject<HTMLDivElement>;
  selectedSearchAsset: SearchAssetResult[number] | null;
  setSelectedSearchAsset: Dispatch<SetStateAction<SearchAssetResult[number] | null>>;
  isLoading: boolean;
  searchAssetNextCursor: () => void;
  setIsOpenPreview: Dispatch<SetStateAction<boolean>>;
  setPreviewAsset: Dispatch<SetStateAction<SearchAssetResult[number] | null>>;
};

export const SearchData = ({
  data,
  searchDataRef,
  selectedSearchAsset,
  setSelectedSearchAsset,
  isLoading,
  searchAssetNextCursor,
  setIsOpenPreview,
  setPreviewAsset,
}: Props) => {
  const handleClickThumbnail = (item: SearchAssetResult[number]) => () => {
    setIsOpenPreview(true);
    setPreviewAsset(item);
  };

  const renderItem = (item: SearchAssetResult[number]) => {
    const handleClickSelect = () => {
      if (selectedSearchAsset !== item) {
        setSelectedSearchAsset(item);
      } else {
        setSelectedSearchAsset(null);
      }
    };

    return (
      <Fragment>
        {
          // FIXME: pixabayのサムネイルがバグっているの暫定対応
          item.thumbnailUrl != null && item.type === 'video' && /pexels/.exec(item.thumbnailUrl) == null ? (
            <VideoThumbnail
              src={item.originalUrl}
              selected={selectedSearchAsset === item}
              hasTitle={item.displayName != null}
              isVideo={item.type === 'video'}
              onClick={handleClickThumbnail(item)}
              onContextMenu={() => false}
            />
          ) : (
            <ImageThumbnail
              src={item.thumbnailUrl || item.lightWeightUrl || '/assets/search_asset_audio.svg'}
              selected={selectedSearchAsset === item}
              hasTitle={item.displayName != null}
              isVideo={item.type === 'video'}
              onClick={handleClickThumbnail(item)}
            />
          )
        }
        <Tooltip title={item.displayName != null ? item.displayName : ''}>
          <TileBar
            label={item.displayName}
            actionIcon={
              <IconButton onClick={handleClickSelect}>
                {selectedSearchAsset === item ? <CheckIcon /> : <BlankIcon />}
              </IconButton>
            }
          />
        </Tooltip>
        {item.type === 'video' && (
          <div className="overlay-button">
            <PlayCircleFilledIcon fontSize="large" />
          </div>
        )}
      </Fragment>
    );
  };

  const renderedData = useMemo(() => {
    return (
      <GridList cols={4}>
        {data.map((item) =>
          item.type === 'video' ? (
            <VideoItemTile selected={selectedSearchAsset === item} key={item.originalUrl}>
              {renderItem(item)}
            </VideoItemTile>
          ) : (
            <ItemTile selected={selectedSearchAsset === item} key={item.originalUrl}>
              {renderItem(item)}
            </ItemTile>
          ),
        )}
      </GridList>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, selectedSearchAsset]);

  return (
    <Root ref={searchDataRef}>
      {renderedData}
      <Waypoint onEnter={() => searchAssetNextCursor()} />
      {isLoading ? <Spinner /> : null}
    </Root>
  );
};

const Root = styled('div')`
  overflow: scroll;
  height: 100%;
`;

const GridList = styled(_GridList)`
  width: 100%;
`;

const ItemTile = styled(GridListTile)<{ selected: boolean }>`
  cursor: pointer;
  text-align: center;

  .MuiGridListTile-tile {
    background-color: #fafafa;
    ${({ selected }) => (selected ? 'border: 3px solid #f4b43e;' : '')}
    ${({ selected }) => (selected ? 'background-color: #f4b43e;' : '')}
  }
`;

const VideoItemTile = styled(ItemTile)<{ selected: boolean }>`
  .MuiGridListTile-tile {
    &:hover {
      background-color: #000000;
    }
  }

  .overlay-button {
    color: #ffffff;
    opacity: 0.7;
    position: absolute;
    top: 5%;
    left: 80%;
    display: none;
  }

  &:hover .overlay-button {
    display: block;
  }
`;

// FIXME: pixabayのバグ対応でImageThumbnailとVideoThumbnail一時的に分けて使用する
const ImageThumbnail = styled('img')<{ selected: boolean; hasTitle: boolean; isVideo: boolean }>`
  width: ${({ hasTitle }) => (hasTitle ? '75' : '100')}%;
  height: ${({ hasTitle }) => (hasTitle ? '75' : '100')}%;
  object-fit: cover;
  opacity: ${({ selected }) => (selected ? 0.7 : 1)};

  &:hover {
    opacity: ${({ isVideo }) => (isVideo ? 0.7 : 1)};
  }
`;

const VideoThumbnail = styled('video')<{ selected: boolean; hasTitle: boolean; isVideo: boolean }>`
  width: ${({ hasTitle }) => (hasTitle ? '75' : '100')}%;
  height: ${({ hasTitle }) => (hasTitle ? '75' : '100')}%;
  object-fit: cover;
  opacity: ${({ selected }) => (selected ? 0.7 : 1)};

  &:hover {
    opacity: ${({ isVideo }) => (isVideo ? 0.7 : 1)};
  }
`;

const TileBarBase = styled(GridListTileBar)`
  .MuiGridListTileBar-titleWrap {
    .MuiGridListTileBar-title {
      font-size: 0.8rem;
    }
  }
`;

const TileBar: FC<{
  label: string | undefined;
  actionIcon: JSX.Element;
  // eslint-disable-next-line react/display-name
}> = forwardRef((props, ref) => {
  // イベントハンドラなどの不足しているpropsは型定義していない
  // Ref: https://material-ui.com/components/tooltips/#custom-child-element
  return <TileBarBase {...props} title={props.label} ref={ref} />;
});

const CheckIcon = styled(CheckBoxOutlineIcon)`
  pointer-events: auto;
  color: white;
`;

const BlankIcon = styled(CheckBoxOutlineBlankIcon)`
  pointer-events: auto;
  color: white;
`;
